const GA_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID || '';

function _hasWindow() {
  /* c8 ignore next */
  return typeof window !== 'undefined' && window !== null;
}

function _hasDocument() {
  /* c8 ignore next */
  return typeof document !== 'undefined' && document !== null;
}

export function isOptedOut() {
  /* c8 ignore next */
  if (!_hasDocument()) return false;
  try {
    return document.cookie.split(';').some((c) => c.trim() === 'ga_opt_out=true');
  } catch (e) {
    return false;
  }
}

export function setOptOut(value) {
  /* c8 ignore next */
  if (!_hasDocument()) return;

  try {
    if (value) {
      // set cookie for 1 year
      document.cookie = 'ga_opt_out=true; max-age=31536000; path=/';
      if (GA_ID && _hasWindow()) {
        try {
          // disable analytics for this measurement id
          // eslint-disable-next-line no-undef
          window['ga-disable-' + GA_ID] = true;
        } catch (e) {
          // ignore
        }
      }
    } else {
      // remove cookie
      document.cookie = 'ga_opt_out=true; max-age=0; path=/';
      if (GA_ID && _hasWindow()) {
        try {
          delete window['ga-disable-' + GA_ID];
        } catch (e) {
          // ignore
        }
      }
    }
  } catch (e) {
    // ignore
  }
}

export function initGA() {
  if (!GA_ID) return;
  if (isOptedOut()) return;
  /* c8 ignore next */
  if (!_hasWindow()) return;

  try {
    const gtag = window.gtag;
    if (typeof gtag === 'function') {
      // do not send automatic page view; we'll send manual events
      gtag('config', GA_ID, { send_page_view: false });
    }
  } catch (e) {
    // swallow any errors to avoid breaking the app
  }
}

export function trackPageView(path, title) {
  if (!GA_ID) return;
  if (isOptedOut()) return;
  /* c8 ignore next */
  if (!_hasWindow()) return;

  try {
    const gtag = window.gtag;
    if (typeof gtag === 'function') {
      gtag('event', 'page_view', { page_path: path, page_title: title });
    }
  } catch (e) {
    // ignore
  }
}

export function trackEvent(eventName, params = {}) {
  if (!GA_ID) return;
  if (isOptedOut()) return;
  /* c8 ignore next */
  if (!_hasWindow()) return;

  try {
    const gtag = window.gtag;
    if (typeof gtag === 'function') {
      gtag('event', eventName, params);
    }
  } catch (e) {
    // ignore
  }
}

// Playback lifecycle helpers
export function trackPlaybackStart(channelCount, clipCount) {
  trackEvent('playback_start', { channel_count: channelCount, clip_count: clipCount });
}

export function trackPlaybackPrevious() {
  trackEvent('playback_previous');
}

export function trackPlaybackNext() {
  trackEvent('playback_next');
}

export function trackPlaybackPause() {
  trackEvent('playback_pause');
}

export function trackPlaybackResume() {
  trackEvent('playback_resume');
}

export function trackPlaybackFullscreenEnter() {
  trackEvent('playback_fullscreen_enter');
}

export function trackPlaybackFullscreenExit() {
  trackEvent('playback_fullscreen_exit');
}

// Ad interaction helpers
// options: { trackingEnabled: boolean }
export function trackPlaybackAdImpression(adUnitId, adDurationSeconds, options = { trackingEnabled: true }) {
  if (!options.trackingEnabled) return;
  trackEvent('playback_ad_impression', { ad_unit_id: adUnitId, ad_duration_seconds: adDurationSeconds });
}

export function trackPlaybackAdClick(adUnitId, options = { trackingEnabled: true }) {
  if (!options.trackingEnabled) return;
  trackEvent('playback_ad_click', { ad_unit_id: adUnitId });
}

export function trackPlaybackAdError(errorReason, options = { trackingEnabled: true }) {
  if (!options.trackingEnabled) return;
  trackEvent('playback_ad_error', { error_reason: errorReason });
}

export function trackPlaybackSkipAd(skipAfterSeconds, options = { trackingEnabled: true }) {
  if (!options.trackingEnabled) return;
  trackEvent('playback_skip_ad', { skip_after_seconds: skipAfterSeconds });
}

// Collection management helpers
export function trackCollectionClipRemoved(clipId, clipCount = 1) {
  trackEvent('collection_clip_removed', { clip_id: clipId, clip_count: clipCount });
}

export function trackCollectionClipTagged(clipId, tagCount) {
  trackEvent('collection_clip_tagged', { clip_id: clipId, tag_count: tagCount });
}

export function trackCollectionClipsBulkTagged(clipCount, tagCount) {
  trackEvent('collection_clips_bulk_tagged', { clip_count: clipCount, tag_count: tagCount });
}

// favorites management helpers
export function trackFavoriteChannelAdded(channelName) {
  trackEvent('favorite_channel_added', { channel_name: channelName });
}

export function trackFavoriteChannelRemoved(channelName) {
  trackEvent('favorite_channel_removed', { channel_name: channelName });
}

export function trackFavoriteChannelsBulkRemoved(channelCount) {
  trackEvent('favorite_channels_bulk_removed', { channel_count: channelCount });
}

// Search & Filter helpers
export function trackSearchPerformed(query, resultCount = 0, filterCount = 0) {
  trackEvent('search_performed', { 
    query_length: query ? query.length : 0,
    result_count: resultCount,
    filter_count: filterCount,
  });
}

export function trackSearchFiltersApplied(filterCount, filterTypes = []) {
  trackEvent('search_filters_applied', {
    filter_count: filterCount,
    filter_types: filterTypes.join(','),
  });
}

// Feature engagement helpers
export function trackFeatureAccessDenied(featureName, requiredTier) {
  trackEvent('feature_access_denied', {
    feature_name: featureName,
    required_tier: requiredTier,
  });
}

export function trackFeatureUsed(featureName, metadata = {}) {
  trackEvent('feature_used', {
    feature_name: featureName,
    ...metadata,
  });
}

export function trackExportFlowStarted(clipCount, source = 'dashboard') {
  trackEvent('export_flow_started', {
    initial_clip_count: clipCount,
    source,
  });
}

// Upgrade & Pricing helpers
export function trackTierComparisonViewed(startingTier) {
  trackEvent('tier_comparison_viewed', { starting_tier: startingTier });
}

export function trackUpgradeInitiated(fromTier, toTier) {
  trackEvent('upgrade_initiated', { from_tier: fromTier, to_tier: toTier });
}

// User settings helpers
export function trackSettingsChanged(settingName, newValue, oldValue) {
  trackEvent('settings_changed', {
    setting_name: settingName,
    new_value: newValue,
    old_value: oldValue,
  });
}

export function trackPrivacyToggleChanged(optedOut) {
  trackEvent('privacy_toggle_changed', { opted_out: optedOut });
}

// Error & Exception helpers
export function trackError(errorName, errorMessage = '', errorContext = {}) {
  trackEvent('error_encountered', {
    error_name: errorName,
    error_message: errorMessage,
    ...errorContext,
  });
}

// Engagement & Navigation helpers
export function trackClipSelected(clipId, clipTitle) {
  trackEvent('clip_selected', { clip_id: clipId, clip_title: clipTitle });
}

export function trackTimeOnPage(pagePath, timeSpentSeconds) {
  trackEvent('page_time_spent', {
    page_path: pagePath,
    time_spent_seconds: timeSpentSeconds,
  });
}
